import React, { useState } from "react";
import { TextField, InputAdornment } from "@material-ui/core";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import { INK, BODY } from "./tokens";

// A money input: groups digits on blur, and takes/emits "" or a Number so
// downstream (lines, base, validatePricing, the POST body) sees plain values.

const group = (n) => Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 });

// Digits, at most one dot, at most two decimals, optional leading minus.
const ACCEPTED = /^-?\d*\.?\d{0,2}$/;

const EMPHASIS = {
  primary: { fontWeight: 700, color: INK },
  secondary: { fontWeight: 400, color: BODY },
};

const MoneyField = ({
  value,
  onChange,
  symbol = null,
  emphasis = "primary",
  error = false,
  label,
  placeholder,
  className = "",
  style,
  fullWidth = true,
  variant = "outlined",
  align = "right",
  disabled = false,
  lock = false,
}) => {
  const [focused, setFocused] = useState(false);
  // Raw text being typed. Only read while focused, so it never has to stay in
  // sync with `value` from outside.
  const [draft, setDraft] = useState("");

  const isEmpty = value === "" || value == null;
  const display = focused ? draft : (isEmpty ? "" : group(value));

  const handleFocus = () => {
    setDraft(isEmpty ? "" : String(value));
    setFocused(true);
  };

  const handleBlur = () => setFocused(false);

  const handleChange = (e) => {
    const raw = e.target.value.replace(/,/g, "").trim();
    // Reject the keystroke rather than rewriting it, so the caret never jumps.
    if (raw !== "" && !ACCEPTED.test(raw)) return;
    setDraft(raw);
    // "", "-" and "." are mid-typing states with no value yet: the draft keeps
    // them on screen, the parent is told the field is empty.
    const n = Number(raw);
    onChange(raw === "" || !Number.isFinite(n) ? "" : n);
  };

  return (
    <TextField
      className={className}
      style={style}
      variant={variant}
      size="small"
      fullWidth={fullWidth}
      type="text"
      label={label}
      placeholder={placeholder}
      error={error}
      disabled={disabled}
      value={display}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      InputLabelProps={label ? { shrink: true } : undefined}
      inputProps={{
        inputMode: "decimal",
        style: {
          textAlign: align,
          fontVariantNumeric: "tabular-nums",
          ...EMPHASIS[emphasis],
        },
      }}
      InputProps={
        symbol || lock
          ? {
              startAdornment: (
                <InputAdornment position="start">
                  {lock && <LockOutlinedIcon style={{ fontSize: 12, marginRight: symbol ? 3 : 0 }} />}
                  {symbol}
                </InputAdornment>
              ),
            }
          : undefined
      }
    />
  );
};

export default MoneyField;
